import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionsAPI, companiesAPI } from '../../services/api';
import { Subscription, SubscriptionStatus, PaymentStatus, Company } from '../../types';
import { useTranslation } from 'react-i18next';
import {
  CreditCardIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  PauseIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';

const SubscriptionsList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [subscriptionsData, companiesData] = await Promise.all([
        subscriptionsAPI.getAll(),
        companiesAPI.getAll(),
      ]);
      
      setSubscriptions(subscriptionsData);
      setCompanies(companiesData);
    } catch (err) {
      setError(t('subscriptions.errorTitle'));
      console.error('Error fetching subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: SubscriptionStatus) => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case SubscriptionStatus.EXPIRED:
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case SubscriptionStatus.CANCELLED:
        return <XCircleIcon className="h-5 w-5 text-gray-500" />;
      case SubscriptionStatus.PENDING:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case SubscriptionStatus.SUSPENDED:
        return <PauseIcon className="h-5 w-5 text-orange-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: SubscriptionStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return `${baseClasses} bg-green-100 text-green-800`;
      case SubscriptionStatus.EXPIRED:
        return `${baseClasses} bg-red-100 text-red-800`;
      case SubscriptionStatus.CANCELLED:
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case SubscriptionStatus.PENDING:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case SubscriptionStatus.SUSPENDED:
        return `${baseClasses} bg-orange-100 text-orange-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getPaymentStatusBadge = (status: PaymentStatus) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case PaymentStatus.PAID:
        return `${baseClasses} bg-green-100 text-green-800`;
      case PaymentStatus.PENDING:
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case PaymentStatus.FAILED:
        return `${baseClasses} bg-red-100 text-red-800`;
      case PaymentStatus.REFUNDED:
        return `${baseClasses} bg-blue-100 text-blue-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const formatCurrency = (amount: number, code?: string) => {
    const currencyCode = code || 'USD';
    return new Intl.NumberFormat(i18n.language || 'en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(Number(amount || 0));
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString(i18n.language || 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getCompanyName = (companyId: string) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown Company';
  };

  const isExpired = (endDate: Date) => {
    return new Date(endDate) < new Date();
  };

  const isExpiringSoon = (endDate: Date) => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return new Date(endDate) <= thirtyDaysFromNow && new Date(endDate) > new Date();
  };

  const handleDelete = async (subscriptionId: string) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    try {
      await subscriptionsAPI.delete(subscriptionId);
      fetchData();
    } catch (err) {
      setError('Failed to delete subscription');
      console.error('Error deleting subscription:', err);
    }
  };

  const filteredSubscriptions = subscriptions.filter(subscription => {
    const matchesStatus = statusFilter === 'all' || subscription.status === statusFilter;
    const matchesPayment = paymentFilter === 'all' || subscription.paymentStatus === paymentFilter;
    const matchesCompany = companyFilter === 'all' || subscription.companyId === companyFilter;
    const matchesSearch = searchTerm === '' || 
      getCompanyName(subscription.companyId).toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscription.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesStatus && matchesPayment && matchesCompany && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('subscriptions.title')}</h1>
          <p className="text-gray-600">{t('subscriptions.manage')}</p>
        </div>
        <Link
          to="/subscriptions/new"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {t('subscriptions.create')}
        </Link>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('subscriptions.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">{t('subscriptions.filters.status')}</option>
              <option value="active">{t('subscriptions.statuses.ACTIVE')}</option>
              <option value="expired">{t('subscriptions.statuses.EXPIRED')}</option>
              <option value="cancelled">{t('subscriptions.statuses.CANCELLED')}</option>
              <option value="pending">{t('subscriptions.statuses.PENDING')}</option>
              <option value="suspended">{t('subscriptions.statuses.SUSPENDED')}</option>
            </select>
          </div>

          {/* Payment Status Filter */}
          <div>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">{t('subscriptions.filters.payment')}</option>
              <option value="paid">{t('subscriptions.paymentStatuses.PAID')}</option>
              <option value="pending">{t('subscriptions.paymentStatuses.PENDING')}</option>
              <option value="failed">{t('subscriptions.paymentStatuses.FAILED')}</option>
              <option value="refunded">{t('subscriptions.paymentStatuses.REFUNDED')}</option>
            </select>
          </div>

          {/* Company Filter */}
          <div>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">{t('subscriptions.filters.company')}</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* Subscriptions List */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredSubscriptions.length === 0 ? (
          <div className="text-center py-12">
            <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('subscriptions.emptyTitle')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? t('subscriptions.emptySearchAdjust') : t('subscriptions.emptyAll')}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <Link
                  to="/subscriptions/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('subscriptions.emptyCta')}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscriptions.table.company')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscriptions.table.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscriptions.table.payment')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscriptions.table.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscriptions.table.period')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscriptions.table.employees')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('subscriptions.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubscriptions.map((subscription) => (
                  <tr key={subscription.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {getCompanyName(subscription.companyId)}
                          </div>
                          {subscription.invoiceNumber && (
                            <div className="text-sm text-gray-500">
                              {t('subscriptions.table.invoice')}: {subscription.invoiceNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(subscription.status)}
                        <span className={`ml-2 ${getStatusBadge(subscription.status)}`}>
                          {subscription.status}
                        </span>
                        {isExpired(subscription.endDate) && (
                          <span className="ml-2 text-xs text-red-600">{t('subscriptions.table.expired')}</span>
                        )}
                        {isExpiringSoon(subscription.endDate) && (
                          <span className="ml-2 text-xs text-yellow-600">{t('subscriptions.table.expiringSoon')}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getPaymentStatusBadge(subscription.paymentStatus)}>
                        {subscription.paymentStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{formatCurrency(subscription.totalAmount, subscription.currency?.code)}</div>
                        <div className="text-gray-500">
                          {t('subscriptions.table.base')}: {formatCurrency(subscription.basePrice, subscription.currency?.code)}
                        </div>
                        <div className="text-gray-500">
                          {t('subscriptions.table.perLocation')}: {formatCurrency(subscription.pricePerLocation, subscription.currency?.code)}
                        </div>
                        <div className="text-gray-500">
                          {t('subscriptions.table.currency')}: {subscription.currency?.code || 'USD'}{subscription.currency?.symbol ? ` (${subscription.currency.symbol})` : ''}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <div>
                          <div>{t('subscriptions.table.start')}: {formatDate(subscription.startDate)}</div>
                          <div>{t('subscriptions.table.end')}: {formatDate(subscription.endDate)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" />
                        {t('subscriptions.table.locationsCount', { count: subscription.activeLocations })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/subscriptions/${subscription.id}`}
                          className="text-primary-600 hover:text-primary-900"
                          title={t('subscriptions.actions.view')}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/subscriptions/${subscription.id}/edit`}
                          className="text-gray-600 hover:text-gray-900"
                          title={t('subscriptions.actions.edit')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(subscription.id)}
                          className="text-red-600 hover:text-red-900"
                          title={t('subscriptions.actions.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionsList;
